import {Trans} from '@lingui/macro';
import React, {FC, useCallback} from 'react';

import {isDefined} from '../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {TabButtonElement} from '../../../analytics/components/tabs/tabButtonElement';
import {AppRoutesPrefixesEnum} from '../../../components/appRoutesPrefixes';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {SettingsApplicationComponentsEnum} from '../../../models/url/settingsUrlModel';
import {getHasAutolinkers} from '../../../store/featureFlags/featureFlagsSelectors';

interface ApplicationsTabProps {
  applicationId?: number;
  componentPage: SettingsApplicationComponentsEnum;
  hasLocalApplication: boolean;
}

export const ApplicationsTab: FC<ApplicationsTabProps> = ({
  applicationId,
  componentPage,
  hasLocalApplication,
}) => {
  const autolinkersEnabled = useAppSelector(getHasAutolinkers);
  const isApplicationFetched = isDefined(applicationId);

  const {navigateToLink} = useLinkNavigation();
  const redirectToTab = useCallback(
    (path: string) =>
      isApplicationFetched
        ? navigateToLink(`/${AppRoutesPrefixesEnum.SETTINGS}/applications/${applicationId}/${path}`)
        : undefined,
    [applicationId, isApplicationFetched, navigateToLink],
  );

  return (
    <>
      <TabButtonElement
        interactionId="settings-applications-edit"
        name={<Trans id="settings-applications-edit">Overview</Trans>}
        isSelected={componentPage === SettingsApplicationComponentsEnum.DETAILS}
        isDisabled={!isApplicationFetched}
        onTabClick={() => redirectToTab(SettingsApplicationComponentsEnum.DETAILS)}
      />
      {autolinkersEnabled && hasLocalApplication && (
        <TabButtonElement
          interactionId="settings-applications-autolinkers"
          name={<Trans id="settings-applications-autolinkers">Autolinkers</Trans>}
          isSelected={componentPage === SettingsApplicationComponentsEnum.AUTOLINKERS}
          isDisabled={!isApplicationFetched}
          onTabClick={() => redirectToTab(SettingsApplicationComponentsEnum.AUTOLINKERS)}
        />
      )}
      <TabButtonElement
        interactionId="settings-applications-servers"
        name={<Trans id="settings-applications-servers">Servers</Trans>}
        isSelected={componentPage === SettingsApplicationComponentsEnum.SERVERS}
        isDisabled={!isApplicationFetched}
        onTabClick={() => redirectToTab(SettingsApplicationComponentsEnum.SERVERS)}
      />
      {hasLocalApplication && (
        <TabButtonElement
          interactionId="settings-applications-previewers"
          name={<Trans id="settings-applications-previewers">Previewers</Trans>}
          isSelected={componentPage === SettingsApplicationComponentsEnum.PREVIEWERS}
          isDisabled={!isApplicationFetched}
          onTabClick={() => redirectToTab(SettingsApplicationComponentsEnum.PREVIEWERS)}
        />
      )}
      {hasLocalApplication && (
        <TabButtonElement
          interactionId="settings-applications-oauth-clients"
          name={<Trans id="settings-applications-oauth-clients">OAuth</Trans>}
          isSelected={componentPage === SettingsApplicationComponentsEnum.OAUTH_CLIENT}
          isDisabled={!isApplicationFetched}
          onTabClick={() => redirectToTab(SettingsApplicationComponentsEnum.OAUTH_CLIENT)}
        />
      )}
    </>
  );
};
